import NextLink from "next/link";
import { Fragment, useMemo } from "react";

import { ModelsCardMedia, SectionsCardGrid } from "@/types";
import { getMediaType } from "@/util/media";

import { cardMediaVariant, type CardMediaVariantProps } from "./card-media.variants";
import { cardMediaTextSize } from "./card-media.text-size";
import { cardMediaIcons } from "./card-media.icons";

export type UseCardMediaProps = CardMediaVariantProps &
  Pick<SectionsCardGrid, "aspectRatio"> & {
    style?: Record<string, string | number>;
    data: ModelsCardMedia;
    appearance: "card" | "grid" | "inline";
    hasWrapper?: boolean;
  };

export const useCardMedia = (props: UseCardMediaProps) => {
  const {
    data: { media, link, icon, asset, ...restData },
    aspectRatio,
    appearance,
    hasWrapper = false,
  } = props;

  const styles = cardMediaVariant({
    aspectRatio,
    appearance,
  });

  const isVideoBackground = !!media && getMediaType(media?.url) === "video";
  const textSize = cardMediaTextSize[appearance];
  const iconName = icon || cardMediaIcons[appearance];
  const hasCustomIcon = !!icon;
  const isDownloadButton = !!asset;
  const defaultWrapperEl = hasWrapper ? "div" : Fragment;
  const WrapperEL = isDownloadButton ? "button" : link ? NextLink : defaultWrapperEl;
  const finalLink = isDownloadButton ? asset?.url : link;

  const handleDownload = async () => {
    if (!asset?.url) return;

    try {
      const response = await fetch(asset.url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = asset.name || `download.${asset.ext || ""}`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      window.open(asset.url, "_blank");
    }
  };

  const elementProps = useMemo(() => {
    if (isDownloadButton) {
      return {
        onClick: handleDownload,
        className: styles.wrapper(),
      };
    }
    if (link) {
      return {
        href: finalLink || "#",
        title: restData.title || "",
        className: styles.wrapper(),
      };
    }
    if (!hasWrapper) {
      return {};
    }
    return { className: styles.wrapper() };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDownloadButton, link, finalLink, restData, styles, hasWrapper]);

  return {
    styles,
    data: { media, link: finalLink, ...restData },
    appearance,
    textSize,
    iconName,
    WrapperEL: WrapperEL as React.ElementType,
    isVideoBackground,
    hasCustomIcon,
    elementProps,
  };
};
